/**
 * pn-reveal.js — the house scroll-reveal + parallax primitive.
 *
 * Why this exists and not Dawn's `scroll-trigger`: Dawn parks the element at
 * `opacity: 0.01` in CSS and only lifts it when its IntersectionObserver fires.
 * Measured on this theme 2026-08-27, chapters 1 and 3 stayed at 0.01 after a
 * programmatic scroll — so the page's primary content was invisible in every
 * captured frame, and would stay invisible for anyone whose animations.js fails.
 *
 * This file cannot have that failure mode, by construction:
 *
 *   · the rest state is a 18px TRANSFORM, never an opacity, so there is no
 *     instant at which any word on the page is unreadable — not to a reader, not
 *     to a screenshot, not to print. See the comment on the rule in penelope.css;
 *   · it lives behind `html.pn-reveal-ready`, and THIS SCRIPT is the only thing
 *     that ever sets that class. No script → no offset at all → the page renders
 *     exactly as it does with motion turned off;
 *   · a 4s failsafe catches the remaining case — class set, observer dead — but
 *     it is CONDITIONAL: it reveals everything only when a target is sitting in
 *     the viewport and still hidden, which can only mean the observer never
 *     fired. A blanket timer would have been worse than useless: it reveals the
 *     whole page a few seconds in whether or not the visitor has scrolled, so
 *     the effect it is meant to protect never actually runs;
 *   · `prefers-reduced-motion: reduce` short-circuits before anything is hidden
 *     and renders the FINAL state, never a different one;
 *   · the theme editor (`Shopify.designMode`) always renders revealed, so a
 *     merchant never sees a blank band while dragging sections.
 *
 * Contract:
 *   [data-pn-reveal]        one element, fades + rises once when it enters view.
 *   [data-pn-reveal-group]  its DIRECT CHILDREN each become a reveal target and
 *                           get `--pn-reveal-i` for the stagger; the group is
 *                           what is observed, so the whole band arrives together.
 *   [data-pn-parallax]      writes a clamped ±16px `--pn-parallax-y` while the
 *                           element crosses the viewport. Compositor-only. Never
 *                           attaches under reduced motion or a coarse pointer.
 */
(() => {
  const REVEALED = 'is-revealed';
  const READY = 'pn-reveal-ready';
  const INSTANT = 'pn-reveal-instant';
  const PARALLAX_MAX = 16;
  const FAILSAFE_MS = 4000;

  const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  let observer = null;
  const revealItems = new Set();
  let scrollBound = false;
  let lastScrollY = 0;
  const parallaxItems = new Set();
  let parallaxBound = false;
  let parallaxTicking = false;

  const childrenOf = (el) => el.__pnRevealTargets || [el];
  const reveal = (el) => childrenOf(el).forEach((node) => node.classList.add(REVEALED));

  /** Collect every reveal target under `root`, wiring group children as we go. */
  function collect(root) {
    const targets = [];

    root.querySelectorAll('[data-pn-reveal-group]').forEach((group) => {
      const kids = Array.from(group.children);
      kids.forEach((kid, i) => {
        kid.setAttribute('data-pn-reveal', '');
        kid.style.setProperty('--pn-reveal-i', String(i));
      });
      group.__pnRevealTargets = kids.length ? kids : [group];
      targets.push(group);
    });

    root.querySelectorAll('[data-pn-reveal]').forEach((el) => {
      // Group children already ride on their group's single observer entry.
      if (el.closest('[data-pn-reveal-group]')) return;
      targets.push(el);
    });

    return targets;
  }

  function revealEverything() {
    document
      .querySelectorAll('[data-pn-reveal], [data-pn-reveal-group]')
      .forEach((el) => el.classList.add(REVEALED));
  }

  /**
   * Fires only if something is sitting INSIDE the viewport and still hidden —
   * which, on a page whose observer works, never happens. Two situations reach
   * it, and both want the same answer:
   *
   *   1. the observer is dead (the degraded case this exists for);
   *   2. the viewport just grew to the height of the whole document, which is
   *      exactly what a full-page screenshot does. Observer callbacks are async
   *      and do not run before the capture, so without this every band below the
   *      original fold photographs blank — in a Lens frame, in a merchant's
   *      screenshot, in a PDF print. Measured 2026-08-28: a 768px full-page
   *      capture came back with images and no text at all.
   *
   * Revealing here is deliberately INSTANT. The 600ms transition would otherwise
   * be caught mid-fade and photograph as half-opacity text.
   */
  function failsafe() {
    const vh = window.innerHeight || 0;
    const stuck = Array.from(document.querySelectorAll('[data-pn-reveal]')).some((el) => {
      if (el.classList.contains(REVEALED)) return false;
      const r = el.getBoundingClientRect();
      return r.top < vh && r.bottom > 0;
    });
    if (!stuck) return;
    document.documentElement.classList.add(INSTANT);
    revealEverything();
  }

  /* ── parallax ──────────────────────────────────────────────────────────── */

  function onParallaxScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(() => {
      parallaxTicking = false;
      const vh = window.innerHeight || 1;
      // Read every rect first, then write — one layout pass, not one per item.
      const writes = [];
      parallaxItems.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const centre = r.top + r.height / 2;
        const travel = (centre - vh / 2) / (vh / 2 + r.height / 2);
        writes.push([el, (Math.max(-1, Math.min(1, travel)) * -PARALLAX_MAX).toFixed(2)]);
      });
      writes.forEach(([el, y]) => el.style.setProperty('--pn-parallax-y', `${y}px`));
    });
  }

  function bindParallax(root) {
    if (prefersReduced() || coarsePointer()) return;
    root.querySelectorAll('[data-pn-parallax]').forEach((el) => {
      parallaxItems.add(el);
      el.classList.add('is-parallax');
    });
    if (parallaxBound || parallaxItems.size === 0) return;
    parallaxBound = true;
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', onParallaxScroll, { passive: true });
    onParallaxScroll();
  }

  /* ── reveal ────────────────────────────────────────────────────────────── */

  function init(root) {
    const scope = root || document;
    bindParallax(scope);

    const targets = collect(scope);
    if (!targets.length) return;

    if (prefersReduced() || inDesignMode() || !('IntersectionObserver' in window)) {
      targets.forEach(reveal);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            observer.unobserve(entry.target);
            revealItems.delete(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0 }
      );
    }
    targets.forEach((el) => {
      observer.observe(el);
      revealItems.add(el);
    });
    bindScrollReveal();
  }

  /**
   * A synchronous belt to the observer's braces.
   *
   * IntersectionObserver callbacks are dispatched asynchronously, so anything
   * that scrolls and then immediately grabs the pixels — Playwright's element
   * screenshots, the Lens scroll-end frames, a user's own screenshot tool — can
   * photograph a band that is on screen but has not been told so yet. This runs
   * inside the scroll event, before the next paint, so by capture time the class
   * is already on. It is the same work the observer does, just not deferred.
   */
  function onRevealScroll() {
    const y = window.scrollY;
    // A person scrolling moves a fraction of a viewport per event. A jump of
    // ~a full screen is a teleport: scrollIntoView, an anchor, End, a scrollbar
    // drag, or a screenshot tool walking the page. Fading in over 600ms after a
    // teleport is wrong for a reader (they did not travel past it) and wrong for
    // a capture (it photographs half-faded — measured on the Matière band at
    // 2026-08-28: facet 3 came back at ~40% opacity). Land it instantly instead.
    const jumped = Math.abs(y - lastScrollY) > (window.innerHeight || 0) * 0.9;
    lastScrollY = y;

    if (!revealItems.size) return;
    const vh = window.innerHeight || 0;
    const done = [];
    revealItems.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) done.push(el);
    });
    if (!done.length) return;

    const root = document.documentElement;
    if (jumped) root.classList.add(INSTANT);
    done.forEach((el) => {
      reveal(el);
      if (observer) observer.unobserve(el);
      revealItems.delete(el);
    });
    if (jumped) {
      // Two frames: one for the revealed state to be recalculated, one for it
      // to paint. Dropping the class earlier re-arms the transition mid-flight.
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove(INSTANT)));
    }
  }

  function bindScrollReveal() {
    if (scrollBound) return;
    scrollBound = true;
    lastScrollY = window.scrollY;
    window.addEventListener('scroll', onRevealScroll, { passive: true });
  }

  function boot() {
    // Hiding only ever happens once this class is on, so a dead script is a
    // no-op rather than a blank page.
    if (!prefersReduced() && !inDesignMode()) {
      document.documentElement.classList.add(READY);
    }
    init(document);
    window.setTimeout(failsafe, FAILSAFE_MS);

    // A viewport that grows past the content is either a real resize (harmless
    // no-op — nothing in view is hidden) or a full-page capture (see failsafe).
    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(failsafe, 60);
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:reorder', () => revealEverything());
})();
